import {SingletonService} from '@/common/dependency.configs'
import {isUndefined} from 'lodash'
import ConstantFactory from '@/common/services/utils/ConstantFactory'
import LanguageFactory from '@/common/services/Language/LanguageFactory'
import {injectable, inject} from 'inversify'
import {GAnalyticsConstant} from '@/common/constant/GAnalyticsConstant'
import FACTORY_MSG from '@/common/messages/factory.messages'
import REGEX from '@/common/enums/regexEnum'

// Must be a plain function declaration so that `arguments` is the IArguments
// object that gtag.js expects — arrow functions and class methods do not bind
// their own `arguments`.
// eslint-disable-next-line prefer-rest-params
function gtag() {
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

@SingletonService
@injectable()
class GAnalyticsFactory {
  private trackingId: string | null
  private isGtm: boolean
  private googleBlocked: boolean

  constructor(
    @inject(ConstantFactory)
    private constantFactory: ConstantFactory,
    @inject(LanguageFactory)
    private languageFactory: LanguageFactory
  ) {
    this.trackingId = null
    this.isGtm = false
    this.googleBlocked = false
  }

  init(workspaceUUID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.trackingId = this.constantFactory.getProp(GAnalyticsConstant.GOOGLE_ANALYTICS_TRACKING_ID)

      if (isUndefined(this.trackingId)) {
        return reject(GAnalyticsConstant.ANALTYICS_NOT_CONFIG)
      } else if (String(this.trackingId).match(REGEX.ANALYTICS_TRACKING_REGEX) == null) {
        return reject(GAnalyticsConstant.ANALTYICS_NOT_FORMAT)
      }

      this.isGtm = String(this.trackingId).startsWith('GTM-')

      if (this.isGtm) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js',
          user_id: workspaceUUID
        })
      }

      const document: any = window.document
      const script = document.createElement('SCRIPT')
      script.async = true
      script.src = (this.isGtm ? GAnalyticsConstant.GTM_URL : GAnalyticsConstant.GOOGLE_MANAGER_URL) + this.trackingId
      script.addEventListener('load', () => {
        if (!this.isGtm) {
          gtag('js', new Date())
          gtag('config', this.trackingId, {user_id: workspaceUUID})
        }
        resolve()
      })

      script.addEventListener('error', async () => {
        this.googleBlocked = true
        console.warn(`Analytics unable to be loaded`)
        return reject(FACTORY_MSG.ANALTYICS.LOAD_ERROR)
      })
      const head = document.head || document.getElementsByTagName('head')[0]
      head.appendChild(script)
    })
  }

  sendWorkflowEvent(state: {state: string; titleKey?: string}) {
    if (this.trackingId && !this.googleBlocked) {
      if (this.isGtm) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({event: 'page_view', page_title: state.state, page_location: location.href})
      } else {
        gtag('event', 'page_view', {
          page_title: state.state,
          page_location: location.href
        })
      }
    }
  }
}
export default GAnalyticsFactory
